<script setup lang="ts">
import ActionBarFiller from '@/components/Interface/DataTable/ActionBar/ActionBarFiller.vue'
import CellFlexWidth from '@/components/Interface/DataTable/Cell/CellFlexWidth.vue'
import CellFixedWidth from '@/components/Interface/DataTable/Cell/CellFixedWidth.vue'
import FerionDataTable from '@/components/Interface/DataTable/FerionDataTable.vue'
import HeaderFixedWidthText from '@/components/Interface/DataTable/Headers/HeaderFixedWidthText.vue'
import HeaderTextFlex from '@/components/Interface/DataTable/Headers/HeaderTextFlex.vue'
import ActionBarSearch from '@/components/Interface/DataTable/ActionBar/ActionBarSearch.vue'
import { useFleetStore } from '@/stores/fleetStore.ts'
import { useTableStore } from '@/stores/tableStore.ts'
import { computed } from 'vue'
import { useShipStore } from '@/stores/shipStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import HeaderFixedWidthTextNoSort from '@/components/Interface/DataTable/Headers/HeaderFixedWidthTextNoSort.vue'
import StandardButton from '@/components/Interface/Buttons/StandardButton.vue'
import IconCount from '@/components/Objects/IconCount.vue'
import { useShipActionStore } from '@/stores/shipActionStore.ts'
import type Fleet from '@/types/Fleet.ts'
import PercentageText from '@/components/Interface/Text/PercentageText.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import FleetAction from '@/components/Interface/ShipFleet/FleetAction.vue'

const fleetStore = useFleetStore()
const tableStore = useTableStore()
const shipStore = useShipStore()
const gameStore = useGameStore()
const shipActionStore = useShipActionStore()

const fleetData = computed(() => {
  let shipCount = []

  for (let ship of shipStore.ships.values()) {
    if (ship.xPos !== gameStore.modalData.xPos || ship.yPos !== gameStore.modalData.yPos) {
      continue
    }
    if (ship.fleetId > 0) {
      shipCount[ship.fleetId] = shipCount[ship.fleetId] ? shipCount[ship.fleetId] + 1 : 1
    }
  }
  return shipCount
})

// Load filtered ships list
const filters = (fleets: Map<number, Fleet>) => {
  return Array.from(fleets.values())
    .filter((fleet) => {
      if (fleetData.value[fleet.id]) {
        return true
      }
    })
    .filter((fleet) => {
      // If no search query, return all fleets
      if (
        !tableStore.mapFleets.filters.nameSearch ||
        tableStore.mapFleets.filters.nameSearch === ''
      ) {
        return true
      }
      // Otherwise, filter based on the fleet name (case-insensitive search)
      return fleet.name
        .toLowerCase()
        .includes(tableStore.mapFleets.filters.nameSearch.toLowerCase())
    })
}

// Double-click event
const doubleClick = (id: number) => {
  const fleet = fleetStore.findFleetById(id)
  if (fleet) {
    shipActionStore.initFleet(fleet)
    tableStore.mapFleets.selectedAll = false
    gameStore.modalType = ''
  }
}

//open ships from fleet in this location
const openFleetShips = (fleetId: number) => {
  gameStore.modalData = {
    xPos: gameStore.modalData.xPos,
    yPos: gameStore.modalData.yPos,
    fleetId: fleetId
  }
  gameStore.modalType = 'mapSelectPlayerFleetShips'
}
</script>

<template>
  <div class="p-1 text-white bg-slate-900">
    <FerionDataTable
      :data="fleetStore.fleets"
      tableStoreName="mapFleets"
      :selector="false"
      :filters="filters"
      :heightLimit="'h-[calc(100vh-23.75rem)]'"
      :double-click="doubleClick"
      :click="doubleClick"
    >
      <!--- ActionBar --->
      <template #actionBar>
        <ActionBarFiller class="mr-1 rounded"><div class="py-3">&nbsp;</div></ActionBarFiller>
        <ActionBarSearch
          v-model="tableStore.mapFleets.filters.nameSearch"
          name="Name"
          placeHolder="Search..."
        />
      </template>
      <!--- Headers --->
      <template #columns="{ sort, sortColumn, sortDirection }">
        <HeaderTextFlex
          min-width="12"
          column="name"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
          class="rounded-tl"
        >
          <span class="pl-2">Name</span>
        </HeaderTextFlex>
        <HeaderFixedWidthText
          width="3"
          align="center"
          column="healthPercentage"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
        >
          <GenericIcon tooltip="Fleet total hit points" type="hit_points" />
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          width="3"
          align="center"
          column="maxSpeed"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
        >
          <GenericIcon tooltip="Fleet max speed" type="max_speed" />
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          width="4"
          align="center"
          column="shields"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
        >
          <GenericIcon tooltip="Fleet shield strength" type="shields" />
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          width="4"
          align="center"
          column="attackShips"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
        >
          <GenericIcon tooltip="Attack strength against ships" type="attack_against_ships" />
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          width="4"
          align="center"
          column="attackPlanets"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
        >
          <GenericIcon tooltip="Attack strength against planets" type="attack_against_planets" />
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          width="3"
          align="center"
          column="scanningRange"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
        >
          <GenericIcon tooltip="Max scanning range" type="scanning_range" />
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          width="3"
          align="center"
          column="repair"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
        >
          <GenericIcon tooltip="Fleet repair capacity" type="repair" />
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          width="6"
          align="center"
          column="nrOfShips"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
        >
          Total ships
        </HeaderFixedWidthText>
        <HeaderFixedWidthTextNoSort width="8" align="center">
          Ships @ {{ gameStore.modalData.xPos }}x{{ gameStore.modalData.yPos }}
        </HeaderFixedWidthTextNoSort>
        <HeaderFixedWidthText
          width="10"
          column="currentActionName"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
          align="center"
          class="rounded-tr"
        >
          Action
        </HeaderFixedWidthText>
      </template>
      <!--- Rows --->
      <template #rows="{ item }">
        <CellFlexWidth min-width="12">
          <div class="flex-col pl-2">
            <div>{{ item.name }}</div>
            <small
              v-if="item.xPos != gameStore.modalData.xPos && item.yPos != gameStore.modalData.yPos"
              class="text-amber-400"
              >Fleet Reinforcements</small
            >
            <small v-else class="text-green-500">Core Fleet</small>
          </div>
        </CellFlexWidth>
        <CellFixedWidth width="3">
          <PercentageText :value="item.healthPercentage" />
        </CellFixedWidth>
        <CellFixedWidth width="3">
          <IconCount :amount="item.maxSpeed" :no-plus="true" />
        </CellFixedWidth>
        <CellFixedWidth width="4">
          <IconCount :amount="item.shields" :no-plus="true" />
        </CellFixedWidth>
        <CellFixedWidth width="4">
          <IconCount :amount="item.attackShips" :no-plus="true" />
        </CellFixedWidth>
        <CellFixedWidth width="4">
          <IconCount :amount="item.attackPlanets" :no-plus="true" />
        </CellFixedWidth>
        <CellFixedWidth width="3">
          <IconCount :amount="item.scanningRange" :no-plus="true" />
        </CellFixedWidth>
        <CellFixedWidth width="3">
          <IconCount :amount="item.repair" :no-plus="true" />
        </CellFixedWidth>
        <CellFixedWidth width="6" align="center">
          {{ item.nrOfShips }}
        </CellFixedWidth>
        <CellFixedWidth width="8" align="center" @click.stop="openFleetShips(item.id)">
          <StandardButton :end-margin="false" :thick="false">{{
            fleetData[item.id]
          }}</StandardButton>
        </CellFixedWidth>
        <CellFixedWidth width="10" align="center">
          <span
            v-if="item.xPos != gameStore.modalData.xPos && item.yPos != gameStore.modalData.yPos"
          >
            Moving to Core Fleet
          </span>
          <span v-else>
            <FleetAction :fleetId="item.id" />
          </span>
        </CellFixedWidth>
      </template>
    </FerionDataTable>
  </div>
</template>

<style scoped lang="scss"></style>
